import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { expandQueryTerms, getPartDiagrams, getPartModels, searchParts } from '../parts/api';
import PelicanPartNumber, { usePelicanPartsLinks } from '../components/parts/PelicanPartNumber';

const DEFAULT_MODEL = 'porsche-boxster-986-1997-2004';
const LAST_MODEL_STORAGE_KEY = 'helpmediy.parts.lastSelectedModel';
const PARTS_RETURN_VIEW_STORAGE_KEY = 'helpmediy.parts.returnView';
const PLACEHOLDER_IMAGE = '/assets/parts/diagram-placeholder.svg';

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (value, terms) => {
  const text = String(value || '');
  const highlightTerms = Array.from(new Set((terms || [])
    .map((term) => String(term).trim().toLowerCase())
    .filter((term) => term.length >= 2)))
    .sort((left, right) => right.length - left.length);
  if (!text || !highlightTerms.length) return text;

  const matchedTerms = new Set(highlightTerms);
  const pieces = text.split(new RegExp(`(${highlightTerms.map(escapeRegex).join('|')})`, 'gi'));
  return (
    <>
      {pieces.map((piece, index) => matchedTerms.has(piece.toLowerCase())
        ? <mark key={`${piece}-${index}`}>{piece}</mark>
        : piece)}
    </>
  );
};

const getSavedModelSlug = () => {
  try {
    return window.localStorage.getItem(LAST_MODEL_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const saveModelSlug = (modelSlug) => {
  try {
    window.localStorage.setItem(LAST_MODEL_STORAGE_KEY, modelSlug);
  } catch (error) {
    // Ignore storage failures, e.g. private browsing restrictions.
  }
};

const getSavedReturnView = () => {
  try {
    const value = window.sessionStorage.getItem(PARTS_RETURN_VIEW_STORAGE_KEY);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const saveReturnView = (view) => {
  try {
    window.sessionStorage.setItem(PARTS_RETURN_VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch (error) {
    // Scroll restoration is a convenience; navigation must still work if storage is unavailable.
  }
};

const clearSavedReturnView = () => {
  try {
    window.sessionStorage.removeItem(PARTS_RETURN_VIEW_STORAGE_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
};

const modelLabel = (model) => {
  if (!model) return 'Porsche model';
  if (model.label) return model.label;
  const code = model.modelCode || model.generation;
  const years = model.yearStart && model.yearEnd ? `${model.yearStart}–${model.yearEnd}` : '';
  return `${model.manufacturer} ${model.model}${code ? ` (${code})` : ''}${model.trim ? ` ${model.trim}` : ''}${years ? ` ${years}` : ''}`;
};

export const dropdownModelLabel = (model) => {
  const withoutManufacturer = modelLabel(model).replace(/^Porsche\s+/i, '').trim();
  const match = withoutManufacturer.match(/^([^()]+?)\s+\(([^()]+)\)(.*)$/);
  if (!match) return withoutManufacturer;
  const [, commonName, parenthesizedCode, suffix] = match;
  return commonName.trim().toLowerCase() === parenthesizedCode.trim().toLowerCase()
    ? `${commonName.trim()}${suffix}`.trim()
    : withoutManufacturer;
};

const diagramUrl = (diagram) => `/parts/porsche/${diagram?.modelSlug || DEFAULT_MODEL}/${diagram?.sectionCode}`;

const DiagramImage = ({ diagram, className }) => (
  <img
    className={className}
    src={diagram?.imageUrl || PLACEHOLDER_IMAGE}
    alt={`${diagram?.sectionCode || ''} ${diagram?.title || 'parts diagram'}`}
    onError={(event) => {
      event.currentTarget.src = PLACEHOLDER_IMAGE;
    }}
  />
);

const VideoPills = ({ videos = [] }) => {
  if (!videos.length) return <span className="parts-no-video">No Help Me DIY video mapped yet</span>;
  return (
    <div className="parts-video-pills">
      {videos.map((video) => (
        <a key={video.videoId} href={video.url} target="_blank" rel="noreferrer noopener">
          ▶ {video.title}
        </a>
      ))}
    </div>
  );
};

const PartsLanding = () => {
  const pelicanPartsLinksEnabled = usePelicanPartsLinks();
  const [initialReturnView] = useState(getSavedReturnView);
  const returnViewRef = useRef(initialReturnView);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(initialReturnView?.selectedModel || DEFAULT_MODEL);
  const [diagrams, setDiagrams] = useState([]);
  const [query, setQuery] = useState(initialReturnView?.query || '');
  const [category, setCategory] = useState(initialReturnView?.category || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchReady, setSearchReady] = useState(!initialReturnView?.query?.trim());
  const [error, setError] = useState('');
  const searchRequestId = useRef(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const modelPayload = await getPartModels();
        const loadedModels = modelPayload.models || [];
        setModels(loadedModels);
        const returnModelSlug = returnViewRef.current?.selectedModel;
        const savedModelSlug = getSavedModelSlug();
        const modelSlug = loadedModels.find((model) => model.slug === returnModelSlug)?.slug
          || loadedModels.find((model) => model.slug === savedModelSlug)?.slug
          || loadedModels.find((model) => model.slug === DEFAULT_MODEL)?.slug
          || loadedModels[0]?.slug
          || DEFAULT_MODEL;
        setSelectedModel(modelSlug);
        const diagramPayload = await getPartDiagrams({
          modelSlug,
          category: returnViewRef.current?.category || '',
        });
        setDiagrams(diagramPayload.diagrams || []);
      } catch (err) {
        setError(err.message || 'Failed to load Porsche parts.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedModel) return;
    getPartDiagrams({ modelSlug: selectedModel, category })
      .then((payload) => setDiagrams(payload.diagrams || []))
      .catch((err) => setError(err.message || 'Failed to load diagrams.'));
  }, [selectedModel, category]);

  useEffect(() => {
    const requestId = searchRequestId.current + 1;
    searchRequestId.current = requestId;
    setResults([]);
    setSearchReady(!query.trim());

    const handle = setTimeout(async () => {
      if (!selectedModel) return;
      if (!query.trim()) {
        if (searchRequestId.current === requestId) {
          setSearching(false);
          setSearchReady(true);
        }
        return;
      }
      try {
        if (searchRequestId.current === requestId) setSearching(true);
        const payload = await searchParts({ query, modelSlug: selectedModel, category });
        if (searchRequestId.current === requestId) {
          setResults(payload.results || []);
        }
      } catch (err) {
        if (searchRequestId.current === requestId) {
          setError(err.message || 'Search failed.');
        }
      } finally {
        if (searchRequestId.current === requestId) {
          setSearching(false);
          setSearchReady(true);
        }
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, selectedModel, category]);

  useEffect(() => {
    const returnView = returnViewRef.current;
    if (!returnView || loading || !searchReady) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId;
    const targetY = Math.max(0, Number(returnView.scrollY) || 0);

    const restore = () => {
      if (cancelled) return;
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      attempts += 1;
      if (maxScrollY >= targetY || attempts >= 30) {
        window.scrollTo({ top: Math.min(targetY, maxScrollY), left: 0, behavior: 'auto' });
        clearSavedReturnView();
        returnViewRef.current = null;
        return;
      }
      timeoutId = window.setTimeout(restore, 50);
    };

    timeoutId = window.setTimeout(restore, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [loading, searchReady, diagrams.length, results.length]);

  const rememberReturnView = () => {
    saveReturnView({
      scrollY: window.scrollY,
      selectedModel,
      query,
      category,
    });
  };

  const selectedModelData = models.find((model) => model.slug === selectedModel) || models[0];
  const queryTerms = useMemo(() => expandQueryTerms(query), [query]);
  const categories = useMemo(
    () => selectedModelData?.categories || [...new Set(diagrams.map((diagram) => diagram.category))],
    [selectedModelData, diagrams]
  );
  const modelsByFamily = useMemo(() => {
    return models.reduce((groups, model) => {
      const key = model.model || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(model);
      return groups;
    }, {});
  }, [models]);

  return (
    <div className="parts-page">
      <section className="parts-hero">
        <div>
          <p className="section-title">Porsche Parts Lookup</p>
          <h2>Find the part number, then watch the repair</h2>
          <p>
            Search {modelLabel(selectedModelData)} diagrams by part name, part number, category, or diagram code.
            Matching Help Me DIY videos appear with the diagram or part row when available.
          </p>
        </div>
      </section>

      {error && <div className="error-banner">{error}</div>}
      {loading ? (
        <p>Loading Porsche parts...</p>
      ) : (
        <>
          <section className="parts-search-panel">
            <label>
              Model / year range
              <select value={selectedModel} onChange={(event) => {
                const modelSlug = event.target.value;
                setSelectedModel(modelSlug);
                saveModelSlug(modelSlug);
                setCategory('');
                setQuery('');
              }}>
                {Object.entries(modelsByFamily).map(([family, familyModels]) => (
                  <optgroup key={family} label={family}>
                    {familyModels.map((model) => (
                      <option key={model.slug} value={model.slug}>
                        {dropdownModelLabel(model)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="parts-query-field">
              Search by part name, number, or diagram code
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Try: brake rotor, 98635240104, 603-00, headlight"
              />
            </label>
            <label>
              Category
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">All categories</option>
                {categories.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>
          </section>

          {query.trim() && (
            <section className="parts-results">
              <h3>{searching ? 'Searching...' : `Search Results (${results.length})`}</h3>
              {results.length === 0 && !searching && <p>No matching parts yet. Try a broader term.</p>}
              <div className="parts-result-list">
                {results.map((result, index) => {
                  const part = result.part;
                  const diagram = result.diagram;
                  return (
                    <div key={`${result.type}-${diagram?.modelSlug}-${diagram?.sectionCode}-${part?.partNumber || index}`} className="parts-result-card">
                      {diagram?.imageUrl && <DiagramImage diagram={diagram} />}
                      <div>
                        <span className="parts-chip">{result.type === 'part' ? 'Part number' : 'Diagram'}</span>
                        <h4>
                          {part ? (
                            <>
                              <PelicanPartNumber partNumber={part.partNumber} enabled={pelicanPartsLinksEnabled}>
                                {highlightText(part.partNumber, queryTerms)}
                              </PelicanPartNumber>{' — '}{highlightText(part.description, queryTerms)}
                            </>
                          ) : <>{highlightText(diagram?.sectionCode, queryTerms)}{' '}{highlightText(diagram?.title, queryTerms)}</>}
                        </h4>
                        <p>
                          <strong>{highlightText(diagram?.sectionCode, queryTerms)}</strong> · {highlightText(diagram?.category, queryTerms)} · {highlightText(diagram?.title, queryTerms)}
                        </p>
                        {part?.fitmentNotes && <p className="parts-muted">{highlightText(part.fitmentNotes, queryTerms)}</p>}
                        <VideoPills videos={part?.videos || diagram?.videos || []} />
                        <Link className="raffle-link-button" to={diagramUrl(diagram)} onClick={rememberReturnView}>
                          Open diagram
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="parts-browse">
            <div className="parts-section-header">
              <h3>Browse {modelLabel(selectedModelData)} diagrams</h3>
              <p>{diagrams.length} diagrams currently available.</p>
            </div>
            <div className="parts-diagram-grid">
              {diagrams.map((diagram) => (
                <Link key={`${diagram.modelSlug}-${diagram.sectionCode}`} className="parts-diagram-card" to={diagramUrl(diagram)} onClick={rememberReturnView}>
                  <DiagramImage diagram={diagram} />
                  <div>
                    <span>{diagram.category}</span>
                    <h4>{diagram.sectionCode}</h4>
                    <p>{diagram.title}</p>
                    <small>{diagram.partCount || 0} parts loaded · {(diagram.videos || []).length} videos</small>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PartsLanding;
